import type {
	ColDef,
	ColumnMovedEvent,
	ICellRendererParams,
	ValueFormatterParams,
} from 'ag-grid-community';
import {
	MyViewTabsEnum,
	addColumn,
	removeColumn,
	setMyMetrics,
	setTabs,
} from 'features/my-view/redux/my-view-slice';
import {
	selectMyMetrics,
	selectRecReviewTab,
	selectTabs,
} from 'features/my-view/redux/selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
	ColumnSources,
	createDynamicRoomTypeRatesColumns,
	forecastColsData,
	inventoryColsData,
	kpiColsData,
	metricsDefaultCols,
	pickupColsData,
	ratesColsData,
} from './recReviewColumns';
import {
	formattedCurrency,
	renderCompRateCell,
	renderLiveMarketRate,
} from './tableCustomCellRender';

import { usePropertyContext } from 'context/propertyContext';
import type { Competitor } from 'graphql/gql-types';
import { SettingsScope } from 'graphql/gql-types';
import { useApolloError } from 'hooks/useApolloError';
import { useUserSettings } from 'hooks/useUserSettings';
import { labels } from 'locales/en.label';
import { useUser } from '../../features/users/context/userContext';
import type { HeaderActionType } from './DashboardCustomHeader';
import { useMyMetrics } from './hooks/useMyMetrics';
import { shouldSeedUniversalMyViewColumns } from './my-view-settings-helpers';
import type { RecReviewData } from './useDashboardController';

export interface ColumnObj extends Omit<ColDef<RecReviewData>, 'headerName'> {
	headerName: string | React.ReactElement;
	field: string;
	headerTooltip: string;
	cellRenderer?: (
		params: any,
	) => JSX.Element | string | number | null | undefined;
	compId?: number;
}

export interface SourceColumn extends ColumnObj {
	source: string;
	headerComponentParams?: {
		actionType: HeaderActionType;
		onClick: (e: any) => void;
	};
}

export const useDashboardColumns = (
	fixedColsLength: number,
	competitors?: Omit<Competitor, 'stars'>[],
	roomTypes?: Array<{ room_category: string; category_code: string }> | null,
	settingsScope: SettingsScope = SettingsScope.property,
) => {
	const {
		property: { propertyId },
	} = usePropertyContext();
	const { user, locale } = useUser();
	const { property } = usePropertyContext();
	const myMetrics = useAppSelector(selectMyMetrics);
	const tabs = useAppSelector(selectTabs);
	const currentTab = useAppSelector(selectRecReviewTab);
	const dispatch = useAppDispatch();
	const { handleApolloError } = useApolloError();

	const [metricsCols, setMetricsCols] = useState(metricsDefaultCols);
	const userId = user?.id;

	const { updateUserHotelMetrics, createUserHotelMetrics } = useMyMetrics({
		propertyId: propertyId,
		userId: user?.id,
	});

	// New settings system for universal/property-specific settings
	const currentSettingsScope =
		settingsScope === 'universal'
			? SettingsScope.universal
			: SettingsScope.property;
	const { getSetting, setSetting } = useUserSettings(currentSettingsScope);
	const hasSeededUniversalColumnsRef = useRef(false);

	const persistLegacyColumns = useCallback(
		async (columnSettings: { myMetrics: any; tabs: any }) => {
			if (!userId || !propertyId) return;

			const metricCols = JSON.stringify({
				myMetrics: columnSettings.myMetrics || [],
				tabs: columnSettings.tabs || {},
			});

			try {
				const updateResult = await updateUserHotelMetrics({
					variables: {
						userId,
						propertyId,
						metricCols,
					},
				});

				const updatedMetrics = updateResult?.data?.updateUserHotelMetrics;
				const updateSucceeded =
					!!updatedMetrics?.user_id &&
					!!updatedMetrics?.property_id &&
					updatedMetrics?.columns != null;

				if (!updateSucceeded) {
					const createResult = await createUserHotelMetrics({
						variables: {
							userId,
							propertyId,
							metricCols,
						},
					});

					if (!createResult?.data?.createUserHotelMetrics?.columns) {
						throw new Error(
							'Legacy my_metrics create/update persistence failed',
						);
					}
				}
			} catch (error) {
				handleApolloError(error as Error);
			}
		},
		[
			userId,
			propertyId,
			updateUserHotelMetrics,
			createUserHotelMetrics,
			handleApolloError,
		],
	);

	const persistColumnSettings = useCallback(
		(columnSettings: { myMetrics: any; tabs: any }) => {
			setSetting('myViewColumns', columnSettings).catch((error) => {
				handleApolloError(error as Error);

				if (currentSettingsScope === SettingsScope.property) {
					persistLegacyColumns(columnSettings);
				}
			});

			if (currentSettingsScope === SettingsScope.property) {
				persistLegacyColumns(columnSettings);
			}
		},
		[setSetting, handleApolloError, currentSettingsScope, persistLegacyColumns],
	);

	// Load settings from new system with fallback to legacy
	const newSystemSettings = useMemo(() => {
		const columnSettings = getSetting('myViewColumns', null) as any;
		if (columnSettings) {
			return {
				myMetrics: columnSettings.myMetrics || [],
				tabs: columnSettings.tabs || {},
			};
		}
		return null;
	}, [getSetting]);

	useEffect(() => {
		if (currentSettingsScope !== SettingsScope.universal) {
			hasSeededUniversalColumnsRef.current = false;
			return;
		}

		if (
			!shouldSeedUniversalMyViewColumns({
				settingsScope: currentSettingsScope,
				hasSeededUniversalColumns: hasSeededUniversalColumnsRef.current,
				newSystemSettings,
				myMetrics,
				tabs,
			})
		) {
			if (newSystemSettings) {
				hasSeededUniversalColumnsRef.current = true;
			}

			return;
		}

		hasSeededUniversalColumnsRef.current = true;
		persistColumnSettings({
			myMetrics: myMetrics || [],
			tabs: tabs || {},
		});
	}, [
		currentSettingsScope,
		myMetrics,
		tabs,
		newSystemSettings,
		persistColumnSettings,
	]);

	useEffect(() => {
		if (propertyId) {
			// Use new settings system if available.
			// Only fall back to legacy property columns when in property scope, so
			// stale property-specific data does not surface while Universal mode is active.
			const allowLegacyFallback =
				currentSettingsScope === SettingsScope.property;
			const effectiveMetrics =
				newSystemSettings?.myMetrics ||
				(allowLegacyFallback ? myMetrics : undefined);
			const effectiveTabs =
				newSystemSettings?.tabs || (allowLegacyFallback ? tabs : undefined);

			const metricCols = effectiveMetrics?.length
				? effectiveMetrics
						.map((col: any) => {
							switch (col.source) {
								case ColumnSources.FORECAST:
									return forecastColsData.find((c) => c.field === col.field);
								case ColumnSources.INVENTORY:
									return inventoryColsData.find((c) => c.field === col.field);
								case ColumnSources.KPI:
									return kpiColsData.find((c) => c.field === col.field);
								case ColumnSources.PICKUP:
									return pickupColsData.find((c) => c.field === col.field);
								case ColumnSources.RATES:
									return ratesColsData.find((c) => c.field === col.field);
								case ColumnSources.COMPETITORS: {
									if (!competitors) return null;
									const compCols = competitorCols(competitors);
									return compCols?.find((c) => c.field === col.field);
								}
								case ColumnSources.ROOM_TYPE_RATES: {
									if (!roomTypes) return null;
									const roomTypeColumns = createDynamicRoomTypeRatesColumns(
										roomTypes,
										locale,
										property?.currencyCode,
									);
									return roomTypeColumns.find((c) => c.field === col.field);
								}
								default:
									return null;
							}
						})
						.flatMap((col: SourceColumn | null) => col ?? [])
				: [];

			if (metricCols?.length) {
				const formattedCols: SourceColumn[] = metricCols.map(
					(col: SourceColumn) => {
						return {
							...col,
							headerComponentParams: {
								actionType: 'remove',
								onClick: (e: React.SyntheticEvent) =>
									removeColumnFromMetric(e, col?.field),
							},
							maxWidth: 120,
						};
					},
				);
				const updateMetricCols = [...formattedCols, ...metricsDefaultCols];
				setMetricsCols(updateMetricCols);
			} else {
				setMetricsCols(metricsDefaultCols);
			}

			// Update Redux with effective settings
			dispatch(setMyMetrics(effectiveMetrics));
			dispatch(setTabs(effectiveTabs));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		tabs,
		propertyId,
		myMetrics,
		competitors,
		newSystemSettings,
		currentSettingsScope,
	]);

	const removeColumnFromMetric = (e: React.SyntheticEvent, field: string) => {
		e.stopPropagation();
		dispatch(removeColumn(field));

		const updatedCols = myMetrics?.filter((col) => col.field !== field);
		const updatedTabs = tabs?.metrics?.filter((col) => col !== field);

		dispatch(setTabs({ ...tabs, metrics: updatedTabs }));

		// Save to new settings system (supports both universal and property-specific scopes)
		const columnSettings = {
			myMetrics: updatedCols,
			tabs: {
				...tabs,
				metrics: updatedTabs,
			},
		};

		persistColumnSettings(columnSettings);
	};

	const handleColumnMoved = useCallback(
		(e: ColumnMovedEvent) => {
			const { column, finished } = e;
			if (finished && column) {
				const columnOrder = e.columnApi
					?.getColumnState()
					.map((col: any) => col.colId)
					.slice(fixedColsLength);

				const tabsCopy = { ...tabs };
				const updatedTabs = {
					...tabsCopy,
					[currentTab]: columnOrder,
				};
				dispatch(setTabs(updatedTabs));

				// Save to new settings system
				const columnSettings = {
					myMetrics: myMetrics || [],
					tabs: updatedTabs,
				};

				persistColumnSettings(columnSettings);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentTab, myMetrics, tabs, persistColumnSettings],
	);

	const addColumnToMetric = useCallback(
		(field: string, source: string) => {
			dispatch(addColumn({ field, source }));

			const otherTabs = tabs ?? {};
			const metricCols = tabs?.metrics ?? metricsCols.map((col) => col.field);

			dispatch(setTabs({ ...otherTabs, metrics: [field, ...metricCols] }));

			// Save to new settings system
			const columnSettings = {
				myMetrics: [{ field, source }, ...(myMetrics || [])],
				tabs: {
					...otherTabs,
					metrics: [field, ...metricCols],
				},
			};

			persistColumnSettings(columnSettings);
		},
		[dispatch, tabs, metricsCols, myMetrics, persistColumnSettings],
	);

	/**
	 * Adds default fields to columns, as well as applies sorting
	 */
	const formatColumns = useCallback(
		(columns: SourceColumn[], source: MyViewTabsEnum) => {
			const tabsKeys = tabs ? Object.keys(tabs) : [];

			columns = columns.map(
				(column): SourceColumn => ({
					...column,
					maxWidth: column.maxWidth || 120,
					headerComponentParams: {
						actionType: metricsCols.some((col) => col.field === column.field)
							? 'added'
							: 'add',
						onClick: (e: React.SyntheticEvent) => {
							e.stopPropagation();
							addColumnToMetric(column.field, column.source);
						},
					},
				}),
			);

			if (!tabs || !tabsKeys.includes(source)) return columns;

			return columns.sort((a, b) => {
				const aIdx = tabs[source]?.indexOf(a.field);
				const bIdx = tabs[source]?.indexOf(b.field);
				if (aIdx === undefined || bIdx === undefined) return 0;
				return aIdx - bIdx;
			});
		},
		[tabs, metricsCols, addColumnToMetric],
	);

	const forecastCols = formatColumns(forecastColsData, MyViewTabsEnum.forecast);
	const inventoryCols = formatColumns(
		inventoryColsData,
		MyViewTabsEnum.inventory,
	);
	const pickupCols = formatColumns(pickupColsData, MyViewTabsEnum.pickup);
	const ratesCols = formatColumns(ratesColsData, MyViewTabsEnum.rates);
	const kpiCols = formatColumns(kpiColsData, MyViewTabsEnum.kpi);

	const roomTypeRatesCols = useMemo(() => {
		// Create dynamic columns based on room types
		const dynamicColumns = createDynamicRoomTypeRatesColumns(
			roomTypes,
			locale,
			property?.currencyCode,
		);
		const formatted = formatColumns(
			dynamicColumns,
			MyViewTabsEnum.roomTypeRates,
		);
		return formatted;
	}, [roomTypes, locale, property?.currencyCode, formatColumns]);

	const competitorCols = (competitors: Omit<Competitor, 'stars'>[]) => {
		if (!competitors) return;

		const competitorColsData: SourceColumn[] = [
			/* TODO:  We don’t have enough snapshots of data yet for this so we will hide it until there is data (B20V-2126).
      {
        headerName: 'Mkt Rate\n(LYST)',
        field: 'market_rate_lyst',
        source: ColumnSources.COMPETITORS,
        headerTooltip: labels.rec_review.dashboard.tooltip.market_rate_lyst,
        decimalFormatter: 2,
        valueFormatter: (params: ValueFormatterParams) => {
          return twoDecimalRateFormatter(params?.value);
        },
      }, */
			{
				headerName: 'Mkt Rate\n(Live)',
				field: 'compRates.market_rate_live',
				source: ColumnSources.COMPETITORS,
				headerTooltip: labels.rec_review.dashboard.tooltip.market_rate_live,
				valueFormatter: (params: ValueFormatterParams) => {
					if (!params?.value) return '-';
					return formattedCurrency(
						params?.value,
						locale,
						property?.currencyCode,
						true,
					);
				},
				cellRenderer: ({ value, data }: ICellRendererParams) => {
					return renderLiveMarketRate({
						value,
						data,
						locale,
						currencyCode: property?.currencyCode,
					});
				},
			},
			{
				headerName: 'Mkt Rate\n(LYF)',
				field: 'market_rate_lyf',
				source: ColumnSources.COMPETITORS,
				headerTooltip: labels.rec_review.dashboard.tooltip.market_rate_lyf,
				valueFormatter: (params: ValueFormatterParams) => {
					if (!params?.value) return '-';
					return formattedCurrency(
						params?.value,
						locale,
						property?.currencyCode,
						true,
					);
				},
			},
		];

		for (const { hotelId, name } of competitors) {
			competitorColsData.push({
				headerName: name,
				field: `compRates.${hotelId}`,
				compId: hotelId,
				source: ColumnSources.COMPETITORS,
				headerTooltip: name,
				cellRenderer: ({ value, data }: ICellRendererParams) =>
					renderCompRateCell(
						value,
						data.current_rate_live ?? data.current_rate,
						locale,
						property?.currencyCode,
					),
			});
		}

		return formatColumns(competitorColsData, MyViewTabsEnum.competitors);
	};

	return {
		metricsCols,
		forecastCols,
		inventoryCols,
		pickupCols,
		ratesCols,
		competitorCols,
		kpiCols,
		roomTypeRatesCols,
		handleColumnMoved,
		addColumnToMetric,
	};
};
